import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, AlertTriangle, ShoppingCart, RefreshCw, Boxes, Info, ChevronDown, ChevronRight, Save, Trash2, CheckCircle2, Archive, RotateCcw, Filter } from "lucide-react";
import { toast } from "sonner";
import ProductStockDialog from "./ProductStockDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const STATUS_LABELS = {
  pending: "En attente",
  in_progress: "En cours",
  completed: "Terminé",
  paused: "En pause",
  cancelled: "Annulé",
  ready: "Prête",
  cashed: "Encaissé",
};

export const StockForecastTab = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], alert_count: 0 });
  const [stockDialog, setStockDialog] = useState({ open: false, product: null });
  const [expanded, setExpanded] = useState(new Set());
  const [editing, setEditing] = useState({}); // material_id → { surface_m2, thickness_mm }
  const [savingId, setSavingId] = useState(null);
  const [actualDialog, setActualDialog] = useState(null); // { material, surface, thickness, saving }
  const [onlyArchived, setOnlyArchived] = useState(false);
  const [onlyWithVariance, setOnlyWithVariance] = useState(false);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.getSupplierStockForecast();
      setData(data);
    } catch (_e) {
      toast.error("Erreur chargement prévisionnel");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleExpand = (productId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const startEdit = (mat) => {
    setEditing((e) => ({
      ...e,
      [mat.material_id]: {
        surface_m2: mat.surface_m2 ?? "",
        thickness_mm: mat.thickness_mm ?? "",
      },
    }));
  };

  const cancelEdit = (matId) => {
    setEditing((e) => {
      const { [matId]: _, ...rest } = e;
      return rest;
    });
  };

  const saveEdit = async (mat) => {
    const draft = editing[mat.material_id];
    if (!draft) return;
    const payload = {
      surface_m2: draft.surface_m2 === "" ? null : parseFloat(draft.surface_m2),
      thickness_mm: draft.thickness_mm === "" ? null : parseFloat(draft.thickness_mm),
    };
    try {
      setSavingId(mat.material_id);
      await api.updateOrderMaterial(mat.material_id, payload);
      toast.success("Quantité mise à jour");
      cancelEdit(mat.material_id);
      await fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de mise à jour");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (mat) => {
    if (!window.confirm(`Retirer "${mat.order_reference || ""}" (${mat.consumed_quantity || 0}) de la consommation ?`)) return;
    try {
      await api.deleteOrderMaterial(mat.material_id);
      toast.success("Ligne retirée");
      await fetch();
    } catch (_e) {
      toast.error("Erreur de suppression");
    }
  };

  const startActualEdit = (mat) => {
    setActualDialog({
      material: mat,
      surface: mat.surface_m2 ?? "",
      thickness: mat.thickness_mm ?? "",
      saving: false,
    });
  };

  const saveActualUsage = async () => {
    if (!actualDialog) return;
    const surf = parseFloat(actualDialog.surface);
    if (!(surf >= 0)) {
      toast.error("Surface réelle invalide");
      return;
    }
    try {
      setActualDialog((s) => ({ ...s, saving: true }));
      await api.recordOrderMaterialActualUsage(actualDialog.material.material_id, {
        actual_surface_m2: surf,
        actual_thickness_mm: actualDialog.thickness === "" ? null : parseFloat(actualDialog.thickness),
      });
      toast.success("Conso réelle enregistrée — ligne archivée et stock mis à jour");
      setActualDialog(null);
      await fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setActualDialog((s) => ({ ...s, saving: false }));
    }
  };

  const restoreToForecast = async (mat) => {
    if (!window.confirm("Annuler l'archivage et remettre cette ligne dans le forecast ?")) return;
    try {
      await api.restoreOrderMaterialToForecast(mat.material_id);
      toast.success("Ligne restaurée");
      await fetch();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="stock-forecast-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge variant={data.alert_count > 0 ? "destructive" : "outline"} className="text-base px-3 py-1" data-testid="alert-count-badge">
            {data.alert_count > 0 ? <AlertTriangle className="h-4 w-4 mr-1 inline" /> : null}
            {data.alert_count} rupture(s)
          </Badge>
          {data.below_threshold_count > 0 && (
            <Badge className="bg-amber-100 text-amber-900 text-base px-3 py-1" data-testid="below-threshold-badge">
              <AlertTriangle className="h-4 w-4 mr-1 inline" />
              {data.below_threshold_count} sous seuil
            </Badge>
          )}
          <span className="text-sm text-muted-foreground">{data.items.length} produit(s) avec activité</span>
        </div>
        <Button size="sm" variant="outline" onClick={fetch} data-testid="refresh-forecast">
          <RefreshCw className="h-4 w-4 mr-1" /> Rafraîchir
        </Button>
      </div>

      {/* Quick filters */}
      <div className="flex items-center gap-4 flex-wrap p-2 border rounded-md bg-slate-50 text-sm" data-testid="forecast-filters">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> Filtres rapides :
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Switch
            checked={onlyArchived}
            onCheckedChange={setOnlyArchived}
            data-testid="filter-only-archived"
          />
          <Archive className="h-3.5 w-3.5 text-slate-500" />
          Lignes archivées uniquement
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Switch
            checked={onlyWithVariance}
            onCheckedChange={setOnlyWithVariance}
            data-testid="filter-only-variance"
          />
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          Avec écart prévu/réel
        </label>
        {(onlyArchived || onlyWithVariance) && (
          <Button
            variant="ghost" size="sm" className="h-6 text-xs ml-auto"
            onClick={() => { setOnlyArchived(false); setOnlyWithVariance(false); }}
            data-testid="filter-reset"
          >
            Réinitialiser
          </Button>
        )}
      </div>

      {data.alert_count > 0 && (
        <Card className="border-red-200 bg-red-50/40">
          <CardContent className="pt-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <strong>{data.alert_count} produit(s) en rupture prévisionnelle</strong> — la consommation des commandes
              en cours dépasse le stock disponible. Commandez les quantités indiquées dans la colonne "À commander".
            </div>
          </CardContent>
        </Card>
      )}

      {data.below_threshold_count > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="pt-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <strong>{data.below_threshold_count} produit(s) sous le seuil minimum</strong> — le stock projeté
              après les commandes en cours passera sous le seuil de sécurité que vous avez configuré.
              Anticipez les commandes pour éviter les ruptures.
            </div>
          </CardContent>
        </Card>
      )}

      {data.items.length === 0 ? (
        <Card>
          <CardContent className="pt-6 pb-6 text-center space-y-2">
            <Info className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground italic">
              Aucun produit n'a encore de stock saisi ni de consommation planifiée sur une commande active.
            </p>
            <p className="text-xs text-muted-foreground">
              Renseignez du stock via l'icône <Boxes className="inline h-3 w-3" /> sur la fiche produit,
              ou verrouillez des produits sur des commandes depuis l'onglet Commandes.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead className="text-right">Stock actuel</TableHead>
                  <TableHead className="text-right">Conso prévue</TableHead>
                  <TableHead className="text-right">Stock projeté</TableHead>
                  <TableHead className="text-right">Seuil min.</TableHead>
                  <TableHead className="text-right">À commander</TableHead>
                  <TableHead>État</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((rowRaw) => {
                  // Apply filters on the orders array
                  const allOrders = rowRaw.orders || [];
                  const filteredOrders = allOrders.filter((o) => {
                    if (onlyArchived && !o.is_archived_from_forecast) return false;
                    if (onlyWithVariance) {
                      if (o.variance_pct === null || o.variance_pct === undefined) return false;
                      if (Math.abs(o.variance_pct) < 0.5) return false;
                    }
                    return true;
                  });
                  const row = { ...rowRaw, orders: filteredOrders };
                  // Skip row entirely if filters active and no matching orders
                  if ((onlyArchived || onlyWithVariance) && filteredOrders.length === 0) {
                    return null;
                  }
                  const isOpen = expanded.has(row.product_id);
                  return [
                  <TableRow
                    key={`row-${row.product_id}`}
                    className={row.alert ? "bg-red-50/60" : row.below_threshold ? "bg-amber-50/60" : ""}
                    data-testid={`forecast-row-${row.product_id}`}
                  >
                    <TableCell>
                      {row.orders && row.orders.length > 0 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => toggleExpand(row.product_id)}
                          data-testid={`forecast-expand-${row.product_id}`}
                          title={isOpen ? "Masquer les commandes" : "Voir les commandes consommatrices"}
                        >
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{row.product_name}</div>
                      {row.product_reference && (
                        <div className="text-xs font-mono text-muted-foreground">{row.product_reference}</div>
                      )}
                      {row.orders && row.orders.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {row.orders.length} commande(s) consommatrice(s)
                          {row.archived_count > 0 && (
                            <Badge variant="secondary" className="ml-1 text-[10px]" title="Lignes archivées (conso réelle déjà saisie)">
                              <Archive className="h-2.5 w-2.5 mr-0.5" />
                              {row.archived_count} archivée(s)
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{row.supplier_name}</TableCell>
                    <TableCell className="text-right font-mono">
                      {row.current_stock} {row.reference_unit}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.upcoming_consumption} {row.reference_unit}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={row.projected_stock < 0 ? "text-red-700 font-semibold" : row.below_threshold ? "text-amber-700 font-semibold" : ""}>
                        {row.projected_stock} {row.reference_unit}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {row.min_stock_threshold != null
                        ? <span>{row.min_stock_threshold} {row.reference_unit}</span>
                        : <span className="text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      {row.to_order > 0
                        ? <span className={row.alert ? "text-red-700" : "text-amber-700"}>{row.to_order} {row.reference_unit}</span>
                        : <span className="text-green-700">0</span>}
                      {row.suggested_order_by_date && row.to_order > 0 && (
                        <div className="text-xs font-normal text-muted-foreground mt-0.5">
                          avant le <span className="font-semibold text-slate-700">{new Date(row.suggested_order_by_date).toLocaleDateString("fr-FR")}</span>
                          {row.supplier_lead_time_days ? <span className="text-xs"> ({row.supplier_lead_time_days}j délai)</span> : null}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.alert
                        ? <Badge className="bg-red-100 text-red-800"><AlertTriangle className="h-3 w-3 mr-1" />Rupture</Badge>
                        : row.below_threshold
                          ? <Badge className="bg-amber-100 text-amber-900"><AlertTriangle className="h-3 w-3 mr-1" />Sous seuil</Badge>
                          : <Badge className="bg-green-100 text-green-800">OK</Badge>}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setStockDialog({
                          open: true,
                          product: {
                            id: row.product_id,
                            name: row.product_name,
                            reference_unit: row.reference_unit,
                          },
                        })}
                        data-testid={`forecast-stock-btn-${row.product_id}`}
                        title="Gérer le stock"
                      >
                        <Boxes className="h-4 w-4 text-amber-600" />
                      </Button>
                    </TableCell>
                  </TableRow>,
                  isOpen && row.orders && row.orders.length > 0 && (
                    <TableRow key={`detail-${row.product_id}`} className="bg-slate-50">
                      <TableCell colSpan={10} className="py-3">
                        <div className="px-3 space-y-2" data-testid={`forecast-orders-${row.product_id}`}>
                          <div className="text-xs font-semibold text-slate-700">
                            Commandes consommatrices ({row.orders.length})
                          </div>
                          <Table className="bg-white border rounded">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-xs">Commande</TableHead>
                                <TableHead className="text-xs">Client</TableHead>
                                <TableHead className="text-xs">Statut</TableHead>
                                <TableHead className="text-xs text-right">Surface (m²)</TableHead>
                                <TableHead className="text-xs text-right">Épaisseur (mm)</TableHead>
                                <TableHead className="text-xs text-right">Conso ({row.reference_unit})</TableHead>
                                <TableHead className="text-xs text-right">Réel / Écart</TableHead>
                                <TableHead className="text-xs text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {row.orders.map((o) => {
                                const isEditing = !!editing[o.material_id];
                                const draft = editing[o.material_id] || {};
                                const archived = !!o.is_archived_from_forecast;
                                return (
                                  <TableRow key={o.material_id} data-testid={`forecast-order-row-${o.material_id}`} className={archived ? "bg-slate-50" : ""}>
                                    <TableCell className="font-mono text-xs">
                                      {o.order_reference || o.order_id.slice(0, 8)}
                                      {archived && (
                                        <Badge variant="secondary" className="ml-1 text-[9px]"><Archive className="h-2 w-2 mr-0.5" />Archivée</Badge>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-xs">{o.client_name || "—"}</TableCell>
                                    <TableCell className="text-xs">
                                      <Badge variant="outline" className="text-[10px]">{STATUS_LABELS[o.order_status] || o.order_status}</Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {isEditing ? (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          className="h-7 text-xs w-24 ml-auto"
                                          value={draft.surface_m2}
                                          onChange={(e) => setEditing((s) => ({ ...s, [o.material_id]: { ...draft, surface_m2: e.target.value } }))}
                                          data-testid={`forecast-input-surface-${o.material_id}`}
                                        />
                                      ) : (
                                        <span className="font-mono text-xs">{o.surface_m2 ?? "—"}</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {isEditing ? (
                                        <Input
                                          type="number"
                                          step="0.1"
                                          className="h-7 text-xs w-20 ml-auto"
                                          value={draft.thickness_mm}
                                          onChange={(e) => setEditing((s) => ({ ...s, [o.material_id]: { ...draft, thickness_mm: e.target.value } }))}
                                          data-testid={`forecast-input-thickness-${o.material_id}`}
                                        />
                                      ) : (
                                        <span className="font-mono text-xs">{o.thickness_mm ?? "—"}</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-xs">
                                      {o.consumed_quantity ?? "—"}
                                    </TableCell>
                                    <TableCell className="text-right text-xs">
                                      {o.actual_consumed_quantity != null ? (
                                        <div className="flex flex-col items-end">
                                          <span className="font-mono">{o.actual_consumed_quantity}</span>
                                          {o.variance_pct !== null && o.variance_pct !== undefined && (
                                            <span
                                              className={`text-[10px] font-semibold ${
                                                Math.abs(o.variance_pct) >= 10 ? "text-red-700"
                                                  : Math.abs(o.variance_pct) >= 5 ? "text-amber-700"
                                                  : "text-emerald-700"
                                              }`}
                                              title="Écart entre prévu et réel"
                                            >
                                              {o.variance_pct > 0 ? "+" : ""}{o.variance_pct}%
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-muted-foreground text-[10px]">—</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <div className="flex justify-end gap-1">
                                        {isEditing ? (
                                          <>
                                            <Button
                                              size="sm"
                                              variant="default"
                                              className="h-7 px-2"
                                              onClick={() => saveEdit(o)}
                                              disabled={savingId === o.material_id}
                                              data-testid={`forecast-save-${o.material_id}`}
                                            >
                                              {savingId === o.material_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-7 px-2"
                                              onClick={() => cancelEdit(o.material_id)}
                                              data-testid={`forecast-cancel-${o.material_id}`}
                                            >
                                              Annuler
                                            </Button>
                                          </>
                                        ) : (
                                          <>
                                            {!archived && (
                                              <Button
                                                size="sm"
                                                variant="default"
                                                className="h-7 px-2 text-xs"
                                                onClick={() => startActualEdit(o)}
                                                data-testid={`forecast-actual-${o.material_id}`}
                                                title="Indiquer la conso réelle (archive la ligne)"
                                              >
                                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                                Conso réelle
                                              </Button>
                                            )}
                                            {!archived && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 px-2 text-xs"
                                                onClick={() => startEdit(o)}
                                                data-testid={`forecast-edit-${o.material_id}`}
                                              >
                                                Modifier
                                              </Button>
                                            )}
                                            {archived && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 px-2 text-xs"
                                                onClick={() => restoreToForecast(o)}
                                                data-testid={`forecast-restore-${o.material_id}`}
                                                title="Annuler l'archivage"
                                              >
                                                <RotateCcw className="h-3 w-3 mr-1" />
                                                Restaurer
                                              </Button>
                                            )}
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-7 px-2"
                                              onClick={() => handleDelete(o)}
                                              data-testid={`forecast-delete-${o.material_id}`}
                                              title="Retirer cette ligne"
                                            >
                                              <Trash2 className="h-3 w-3 text-red-600" />
                                            </Button>
                                          </>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  ),
                ];
                }).flat().filter(Boolean)}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-4 text-xs text-muted-foreground space-y-1">
          <div>
            <strong>Comment ça marche :</strong> L'application liste les produits qui ont
            du stock saisi OU une consommation planifiée. Toutes les commandes (sauf annulées)
            sont prises en compte. Les lignes pour lesquelles tu as renseigné la
            <Badge variant="outline" className="mx-1 text-xs">conso réelle</Badge>
            sont archivées et n'impactent plus le forecast.
          </div>
          <div>
            <strong>À commander = Conso prévue − Stock actuel</strong> (minimum 0). Une alerte rouge
            s'affiche dès que la conso dépasse le stock.
          </div>
          <div>
            <strong>À commander</strong> : calculé pour combler la différence entre la consommation prévue
            et le stock, <em>ou</em> pour remonter le stock projeté au-dessus du seuil minimum (si configuré
            sur le produit). Statut <Badge className="mx-1 text-xs bg-red-100 text-red-800">Rupture</Badge>
            si rupture réelle, <Badge className="mx-1 text-xs bg-amber-100 text-amber-900">Sous seuil</Badge>
            si stock projeté &lt; seuil configuré.
          </div>
          <div className="text-right">
            <Button
              variant="outline" size="sm"
              onClick={() => {
                const rows = [["Fournisseur", "Produit", "Référence", "Stock actuel", "Unité", "Conso prévue", "Stock projeté", "Seuil min.", "À commander", "État"]];
                data.items.forEach(r => rows.push([
                  r.supplier_name, r.product_name, r.product_reference || "",
                  r.current_stock, r.reference_unit, r.upcoming_consumption,
                  r.projected_stock, r.min_stock_threshold ?? "", r.to_order,
                  r.alert ? "RUPTURE" : r.below_threshold ? "SOUS SEUIL" : "OK",
                ]));
                const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
                const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `ordre-commande-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success("CSV téléchargé");
              }}
              data-testid="export-forecast-csv"
            >
              <ShoppingCart className="h-4 w-4 mr-1" /> Export CSV (ordre de commande)
            </Button>
          </div>
        </CardContent>
      </Card>

      <ProductStockDialog
        open={stockDialog.open}
        product={stockDialog.product}
        onClose={() => { setStockDialog({ open: false, product: null }); fetch(); }}
      />

      {/* Actual usage dialog */}
      <Dialog open={!!actualDialog} onOpenChange={(o) => !o && setActualDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="actual-usage-dialog">
          <DialogHeader>
            <DialogTitle>Indiquer la consommation réelle</DialogTitle>
          </DialogHeader>
          {actualDialog && (
            <div className="space-y-3 text-sm">
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                <div><b>Commande :</b> {actualDialog.material.order_reference || "—"} · {actualDialog.material.client_name || "—"}</div>
                <div className="mt-1">
                  <b>Prévu :</b> {actualDialog.material.surface_m2 ?? "—"} m² ·
                  {" "}{actualDialog.material.thickness_mm ?? "—"} mm
                  {" "}→ <b>{actualDialog.material.consumed_quantity ?? "—"}</b>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Surface réellement utilisée (m²)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={actualDialog.surface}
                    onChange={(e) => setActualDialog((s) => ({ ...s, surface: e.target.value }))}
                    data-testid="actual-surface-input"
                    autoFocus
                  />
                </div>
                <div>
                  <Label className="text-xs">Épaisseur réelle (mm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={actualDialog.thickness}
                    onChange={(e) => setActualDialog((s) => ({ ...s, thickness: e.target.value }))}
                    data-testid="actual-thickness-input"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground italic">
                💡 Le delta entre prévu et réel reste dans ton stock disponible.
                Une entrée de stock négative correspondant à la conso réelle est créée
                automatiquement. La ligne sera archivée du forecast.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setActualDialog(null)}>Annuler</Button>
            <Button
              onClick={saveActualUsage}
              disabled={actualDialog?.saving}
              data-testid="actual-save-btn"
            >
              {actualDialog?.saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Confirmer + archiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockForecastTab;
